import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CreditCard, Check, Loader2, Users, Sparkles } from "lucide-react";

export default function Billing() {
  const { user, refresh } = useAuth();
  const isMgr = user?.role === "manager" || user?.role === "super_admin";
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = async () => setStatus((await api.get("/billing/status")).data);
  useEffect(() => { load(); }, []);

  const payWithPaystack = async (plan) => {
    setBusy("ps_" + plan);
    try {
      const { data } = await api.post("/billing/paystack/initialize", { plan, callback_url: `${window.location.origin}/payment/callback` });
      if (data.activated) { toast.success("Your trial is active."); await refresh?.(); load(); setBusy(null); return; }
      window.location.href = data.authorization_url;
    }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); setBusy(null); }
  };
  const payWithPaypal = async (plan) => {
    setBusy("pp_" + plan);
    try { const { data } = await api.post("/billing/paypal/initialize", { plan, return_origin: window.location.origin }); window.location.href = data.approve_url; }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); setBusy(null); }
  };

  if (!status) return <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-emerald-500" /></div>;

  const active = status.subscription_status === "active";
  const current = status.current_tier;
  const isCustom = current?.key === "custom";
  const paystackOn = status.providers?.paystack?.enabled;
  const paypalOn = status.providers?.paypal?.enabled;
  const freeMode = status.free_mode;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2"><CreditCard className="w-5 h-5 text-emerald-400" /><h1 className="font-display text-2xl font-bold">Billing & Subscription</h1></div>

      <div className="card-emerald p-6 flex flex-wrap items-center justify-between gap-4" data-testid="billing-status-card">
        <div>
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Current status</p>
          <div className="flex items-center gap-2 mt-1">
            <Badge className={active ? "bg-emerald-500/20 text-emerald-300" : status.subscription_status === "suspended" ? "bg-red-500/20 text-red-300" : "bg-amber-500/20 text-amber-300"}>{status.subscription_status === "trial_pending" ? "trial not started" : status.subscription_status}</Badge>
            {freeMode && <Badge className="bg-sky-500/20 text-sky-300" data-testid="free-mode-badge">Free access granted</Badge>}
            {status.subscription_status === "trialing" && status.trial_days_left != null && <span className="text-sm text-muted-foreground">{status.trial_days_left} days left in trial</span>}
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-1 justify-end"><Users className="w-3.5 h-3.5" /> Active employees</p>
          <p className="font-display text-2xl font-extrabold mt-1" data-testid="active-employee-count">{status.active_employee_count}</p>
          <p className="text-xs text-emerald-300">{current?.label}</p>
        </div>
      </div>

      {status.discount && (
        <div className="card-emerald p-4 border-emerald-500/40 flex items-center gap-2" data-testid="billing-discount-banner">
          <Sparkles className="w-4 h-4 text-emerald-400 shrink-0" />
          <p className="text-sm">A discount of <span className="font-semibold text-emerald-300">{status.discount.type === "percent" ? `${status.discount.value}%` : `$${status.discount.value}`}</span> is active and applied automatically at checkout until <span className="font-semibold">{status.discount.end_date}</span>.</p>
        </div>
      )}

      {!isMgr ? (
        <div className="card-emerald p-8 text-center text-muted-foreground">Only company managers can manage the subscription.</div>
      ) : (
        <>
          {status.trial_pending && (
            <div className="card-emerald p-6 border-emerald-500/50 flex flex-wrap items-center justify-between gap-4" data-testid="trial-activation-card">
              <div>
                <p className="font-display text-xl font-bold">Start your {status.trial_days}-day trial</p>
                <p className="text-sm text-muted-foreground mt-1">Full access to every feature for <span className="text-emerald-300 font-semibold" data-testid="trial-price">${status.trial_price_usd.toFixed(2)}</span>.</p>
              </div>
              <Button onClick={() => payWithPaystack("trial")} disabled={busy !== null || !paystackOn} className="bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold px-6" data-testid="start-trial-btn">
                {busy === "ps_trial" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Start trial"}
              </Button>
            </div>
          )}
          <p className="text-sm text-muted-foreground">Your plan is set automatically by your active employee count. You're on the <span className="text-emerald-300 font-semibold">{current?.label}</span> tier.</p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {status.tiers.map((t) => {
              const isCurrent = t.key === current?.key;
              return (
                <div key={t.key} className={`card-emerald p-6 relative ${isCurrent ? "border-emerald-500/50 glow-emerald" : "opacity-80"}`} data-testid={`tier-${t.key}`}>
                  {isCurrent && <span className="absolute -top-3 left-6 px-3 py-1 rounded-full bg-emerald-500 text-[#062217] text-xs font-bold">Your plan</span>}
                  <p className="text-sm font-medium">{t.label}</p>
                  {t.key === "custom" ? (
                    <>
                      <p className="font-display text-3xl font-extrabold mt-3">Custom</p>
                      <p className="text-sm text-muted-foreground mt-1">Contact our sales team for volume pricing.</p>
                      {isCurrent && <Button className="w-full mt-4" variant="outline" onClick={() => toast.info("Our team will reach out at " + user.email)} data-testid="contact-sales-btn">Contact sales</Button>}
                    </>
                  ) : (
                    <>
                      <div className="mt-3 flex items-end gap-1"><span className="font-display text-3xl font-extrabold">${t.monthly}</span><span className="text-muted-foreground mb-1 text-sm">/mo</span></div>
                      <p className="text-xs text-muted-foreground mt-1">or ${t.annual_monthly}/mo billed annually (${t.annual}/yr)</p>
                      {isCurrent && (
                        <div className="mt-4 space-y-2" data-testid="pay-options">
                          {paystackOn && (
                            <>
                              <p className="text-[11px] uppercase tracking-widest text-emerald-300/80">Pay with Paystack</p>
                              <Button onClick={() => payWithPaystack("monthly")} disabled={busy !== null} className="w-full bg-emerald-500 hover:bg-emerald-600 text-[#062217] font-semibold" data-testid="pay-paystack-monthly-btn">{busy === "ps_monthly" ? <Loader2 className="w-4 h-4 animate-spin" /> : `Pay $${t.monthly}/mo`}</Button>
                              <Button onClick={() => payWithPaystack("annual")} disabled={busy !== null} variant="outline" className="w-full border-emerald-500/40" data-testid="pay-paystack-annual-btn">{busy === "ps_annual" ? <Loader2 className="w-4 h-4 animate-spin" /> : `Pay $${t.annual}/yr`}</Button>
                            </>
                          )}
                          {paypalOn && (
                            <div className={`space-y-2 ${paystackOn ? "pt-3 mt-1 border-t border-white/5" : ""}`}>
                              <p className="text-[11px] uppercase tracking-widest text-muted-foreground">{paystackOn ? "Or pay with PayPal" : "Pay with PayPal"}</p>
                              <Button onClick={() => payWithPaypal("monthly")} disabled={busy !== null} variant="outline" className="w-full border-emerald-500/20" data-testid="pay-paypal-monthly-btn">{busy === "pp_monthly" ? <Loader2 className="w-4 h-4 animate-spin" /> : `Pay $${t.monthly}/mo · PayPal`}</Button>
                            </div>
                          )}
                          {!paystackOn && !paypalOn && <p className="text-xs text-muted-foreground">No payment method is currently available. Please contact support.</p>}
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </div>
          <div className="card-emerald p-5">
            <p className="text-sm font-medium flex items-center gap-2"><Sparkles className="w-4 h-4 text-emerald-400" /> All plans include</p>
            <div className="grid sm:grid-cols-2 gap-2 mt-3 text-sm text-muted-foreground">
              {["Workplace QR attendance", "Unlimited tasks & leave", "Payroll & PDF payslips", "Announcements & meetings", "Audit-ready records", "Email support"].map((f) => (
                <div key={f} className="flex items-center gap-2"><Check className="w-4 h-4 text-emerald-400" /> {f}</div>
              ))}
            </div>
          </div>
          <p className="text-xs text-muted-foreground text-center">Prices are set server-side from your active employee count. {paystackOn ? "Paystack is the primary payment method" : paypalOn ? "PayPal is the primary payment method (USD)" : "Payment methods are being set up"}{paystackOn && paypalOn ? " · PayPal also available" : ""}.</p>
        </>
      )}
    </div>
  );
}
